'use client';

import { useRef } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import cx from 'clsx';

import colors from '@/styles/themes/colors';
import { SolutionColor } from '@/types/solution.type';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';

import styles from './AIShowcaseItem.module.scss';

export interface AISolution {
  title: string;
  description: string;
  features: string[];
}

interface AIShowcaseItemProps {
  solution: AISolution;
  color?: SolutionColor;
}

const AIShowcaseItem = ({ solution, color = 'blue' }: AIShowcaseItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(ref);
  const colorClass = styles[`color${color.charAt(0).toUpperCase()}${color.slice(1)}`];

  return (
    <Box ref={ref} className={cx(styles.item, colorClass, { [styles.visible]: isVisible })}>
      <Typography
        variant="subtitle1"
        component="h3"
        classes={{ root: styles.titleLabelRoot }}
        className={cx(styles.titleLabel, colorClass)}
        fontWeight={500}
      >
        {solution.title}
      </Typography>
      <Typography variant="h4" component="h4" color={colors.white} fontWeight={400}>
        {solution.description}
      </Typography>
      <Stack component="ul" spacing={3} className={styles.featuresList}>
        {solution.features.map(feature => (
          <Box component="li" key={feature} className={styles.featureItem}>
            <Typography variant="body1" color={colors.graytone200}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default AIShowcaseItem;
